import { z } from "zod";
import { businessUnitTypeSchema } from "../auth/features.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, nonNegativeMoneySchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
import { postingKeyNameSchema, postingKeyRefSchema } from "./posting-keys.js";
// ── Ortak sabitler ───────────────────────────────────────────────────────────
export const ACCOUNT_TYPES = ["ASSET", "LIABILITY", "EQUITY", "REVENUE", "EXPENSE", "MEMO"];
export const accountTypeSchema = z.enum(ACCOUNT_TYPES);
export const BALANCE_SIDES = ["DEBIT", "CREDIT"];
export const balanceSideSchema = z.enum(BALANCE_SIDES);
/** Fiş satırının taşıyabildiği analitik boyutlar. Zorunluluk HESAP tanımından gelir. */
export const DIMENSIONS = ["STATION", "BUSINESS_UNIT", "COUNTERPARTY", "COST_CENTER", "INSTRUMENT"];
export const dimensionSchema = z.enum(DIMENSIONS);
export const FISCAL_PERIOD_STATUSES = ["OPEN", "SOFT_CLOSED", "CLOSED"];
export const fiscalPeriodStatusSchema = z.enum(FISCAL_PERIOD_STATUSES);
export const JOURNAL_ENTRY_TYPES = ["TAHSIL", "TEDIYE", "MAHSUP", "ACILIS", "KAPANIS"];
export const journalEntryTypeSchema = z.enum(JOURNAL_ENTRY_TYPES);
export const JOURNAL_ENTRY_STATUSES = ["DRAFT", "POSTED", "REVERSED"];
export const journalEntryStatusSchema = z.enum(JOURNAL_ENTRY_STATUSES);
const accountCodeSchema = z
    .string()
    .trim()
    .regex(/^\d{1,3}(\.\d{1,6}){0,4}$/, "Hesap kodu 100, 100.01 ya da 100.01.003 biçiminde olmalıdır");
const companyQuery = z.object({ companyId: uuidSchema });
const queryBoolean = z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true");
// ── Hesap planı ──────────────────────────────────────────────────────────────
export const accountSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    code: z.string(),
    name: z.string(),
    parentId: uuidSchema.nullable(),
    level: z.number().int(),
    accountType: accountTypeSchema,
    normalBalance: balanceSideSchema,
    isPostable: z.boolean(),
    requiredDimensions: z.array(dimensionSchema),
    isActive: z.boolean(),
    version: z.number().int(),
});
export const accountListSchema = z.object({ items: z.array(accountSchema) });
export const listAccountsQuerySchema = companyQuery.extend({
    search: z.string().trim().max(100).optional().describe("Kod ya da adla arama"),
    postableOnly: queryBoolean.describe("Yalnızca kayda açık (yaprak) hesaplar"),
    includeInactive: queryBoolean,
    limit: z.coerce.number().int().min(1).max(2000).optional().describe("Otomatik tamamlama için üst sınır"),
});
export const createAccountSchema = z.object({
    companyId: uuidSchema,
    parentId: uuidSchema.describe("Üst hesap; yeni hesabın kodu üst hesabın koduyla başlamalıdır"),
    code: accountCodeSchema,
    name: z.string().trim().min(1).max(200),
    requiredDimensions: z.array(dimensionSchema).max(5).default([]),
});
export const updateAccountSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    requiredDimensions: z.array(dimensionSchema).max(5).optional(),
    isActive: z.boolean().optional(),
    version: z.number().int(),
});
export const accountUsageSchema = z.object({
    lineCount: z.number().int().describe("Bu hesaba atılmış fiş satırı sayısı (taslaklar dâhil)"),
    childCount: z.number().int(),
    mappings: z.array(z.object({
        id: uuidSchema,
        postingKey: z.string(),
        keyParam: z.string().nullable(),
        stationId: uuidSchema.nullable(),
        businessUnit: businessUnitTypeSchema.nullable(),
    })),
});
// ── Masraf merkezleri ────────────────────────────────────────────────────────
export const costCenterSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    code: z.string(),
    name: z.string(),
    isActive: z.boolean(),
    version: z.number().int(),
});
export const costCenterListSchema = z.object({ items: z.array(costCenterSchema) });
export const listCostCentersQuerySchema = companyQuery;
export const createCostCenterSchema = z.object({
    companyId: uuidSchema,
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9][A-Z0-9_.-]{0,29}$/, "Kod büyük harf, rakam ve . _ - içerebilir"),
    name: z.string().trim().min(1).max(200),
});
export const updateCostCenterSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    isActive: z.boolean().optional(),
    version: z.number().int(),
});
// ── Şirket muhasebe ayarları ─────────────────────────────────────────────────
export const SALES_POSTING_MODES = ["REALTIME", "DAILY_SUMMARY"];
export const accountingSettingsSchema = z.object({
    companyId: uuidSchema,
    coaTemplateCode: z.string().nullable(),
    salesPostingMode: z.enum(SALES_POSTING_MODES),
    isInitialized: z.boolean().describe("Hesap planı şablondan kopyalanmış mı"),
    version: z.number().int(),
});
export const updateAccountingSettingsSchema = z.object({
    salesPostingMode: z.enum(SALES_POSTING_MODES).optional(),
    version: z.number().int(),
});
// ── Mali yıl ve dönemler ─────────────────────────────────────────────────────
export const fiscalPeriodSchema = z.object({
    id: uuidSchema,
    fiscalYearId: uuidSchema,
    periodNo: z.number().int(),
    startDate: localDateSchema,
    endDate: localDateSchema,
    status: fiscalPeriodStatusSchema,
    statusChangedAt: timestampSchema.nullable(),
    statusReason: z.string().nullable(),
    draftCount: z.number().int().describe("Bu dönemdeki kesinleşmemiş taslak fiş sayısı"),
    postedCount: z.number().int(),
    version: z.number().int(),
});
export const fiscalYearSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    year: z.number().int(),
    startDate: localDateSchema,
    endDate: localDateSchema,
    status: z.enum(["OPEN", "CLOSED"]),
    periods: z.array(fiscalPeriodSchema),
    version: z.number().int(),
});
export const fiscalYearListSchema = z.object({ items: z.array(fiscalYearSchema) });
export const listFiscalYearsQuerySchema = companyQuery;
export const createFiscalYearSchema = z.object({
    companyId: uuidSchema,
    year: z
        .number()
        .int()
        .min(2000)
        .max(2999)
        .describe("Takvim yılı (1 Ocak – 31 Aralık), 12 aylık dönemle açılır"),
});
export const changePeriodStatusSchema = z
    .object({
    status: fiscalPeriodStatusSchema,
    reason: z.string().trim().min(5).max(500).nullable().default(null),
    version: z.number().int(),
})
    .describe("Dönemi yeniden AÇMAK (daha açık bir duruma almak) gerekçe ve yeniden kimlik doğrulama ister");
export const PERIOD_CHECK_KEYS = [
    "PENDING_POSTING_EXCEPTIONS",
    "DRAFT_ENTRIES",
    "LEDGER_BALANCED",
    "SUBLEDGER_COUNTERPARTY",
    "SUBLEDGER_INSTRUMENT",
    "PREVIOUS_PERIODS_CLOSED",
];
export const periodChecklistSchema = z.object({
    periodId: uuidSchema,
    canClose: z.boolean(),
    items: z.array(z.object({
        key: z.enum(PERIOD_CHECK_KEYS),
        status: z.enum(["OK", "WARNING", "BLOCKER", "NOT_APPLICABLE"]),
        count: z.number().int(),
        detail: z.string(),
    })),
});
// ── Fişler ───────────────────────────────────────────────────────────────────
const lineDimensionsShape = {
    stationId: uuidSchema.nullable().default(null),
    businessUnit: businessUnitTypeSchema.nullable().default(null),
    counterpartyId: uuidSchema.nullable().default(null),
    costCenterId: uuidSchema.nullable().default(null),
    instrumentId: uuidSchema.nullable().default(null),
};
const lineDocumentShape = {
    docType: z.string().trim().max(40).nullable().default(null),
    docNo: z.string().trim().max(60).nullable().default(null),
    docDate: localDateSchema.nullable().default(null),
};
export const journalLineInputSchema = z
    .object({
    accountId: uuidSchema,
    debit: nonNegativeMoneySchema.default("0"),
    credit: nonNegativeMoneySchema.default("0"),
    description: z.string().trim().max(500).nullable().default(null),
    ...lineDimensionsShape,
    ...lineDocumentShape,
})
    .refine((line) => BigInt(line.debit) > 0n !== BigInt(line.credit) > 0n, {
    message: "Satırda borç ya da alacaktan yalnızca biri sıfırdan büyük olmalıdır",
    path: ["debit"],
});
/** Taslak kaydetme: dengesiz olabilir. Kesinleştirme ayrı adımdır ve tüm kuralları uygular. */
export const saveJournalEntrySchema = z.object({
    companyId: uuidSchema,
    entryType: journalEntryTypeSchema.default("MAHSUP"),
    documentDate: localDateSchema,
    postingDate: localDateSchema,
    description: z.string().trim().min(1).max(500),
    lines: z.array(journalLineInputSchema).min(1).max(2000),
});
/** PUT: taslağın TÜMÜ (satırlar dâhil) değiştirilir; kısmi güncelleme değildir. */
export const replaceJournalEntrySchema = saveJournalEntrySchema
    .omit({ companyId: true })
    .extend({ version: z.number().int() });
export const postJournalEntrySchema = z.object({ version: z.number().int() });
export const reverseJournalEntrySchema = z.object({
    postingDate: localDateSchema.describe("Ters kaydın tarihi; AÇIK bir döneme düşmelidir"),
    description: z.string().trim().min(1).max(500).nullable().default(null),
});
export const journalLineSchema = z.object({
    id: uuidSchema,
    lineNo: z.number().int(),
    accountId: uuidSchema,
    accountCode: z.string(),
    accountName: z.string(),
    debit: moneySchema,
    credit: moneySchema,
    description: z.string().nullable(),
    stationId: uuidSchema.nullable(),
    stationName: z.string().nullable(),
    businessUnit: businessUnitTypeSchema.nullable(),
    counterpartyId: uuidSchema.nullable(),
    costCenterId: uuidSchema.nullable(),
    costCenterName: z.string().nullable(),
    instrumentId: uuidSchema.nullable(),
    docType: z.string().nullable(),
    docNo: z.string().nullable(),
    docDate: localDateSchema.nullable(),
    currency: z.string(),
    postingKey: z.string().nullable(),
    sourceCount: z.number().int().describe("Özet fişte bu satırı oluşturan kaynak işlem sayısı"),
});
export const journalEntrySummarySchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    entryNo: z.string().nullable().describe("Yevmiye madde no; taslakta null"),
    fiscalYear: z.number().int(),
    entryType: journalEntryTypeSchema,
    documentDate: localDateSchema,
    postingDate: localDateSchema,
    description: z.string(),
    source: z.string(),
    sourceDocType: z.string().nullable(),
    sourceDocId: uuidSchema.nullable(),
    isSummary: z.boolean(),
    status: journalEntryStatusSchema,
    totalDebit: moneySchema,
    totalCredit: moneySchema,
    lineCount: z.number().int(),
    reversalOfId: uuidSchema.nullable(),
    reversedById: uuidSchema.nullable(),
    createdByName: z.string().nullable(),
    postedAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const journalEntrySchema = journalEntrySummarySchema.extend({
    periodStatus: fiscalPeriodStatusSchema,
    lines: z.array(journalLineSchema),
});
export const journalEntryPageSchema = pageOf(journalEntrySummarySchema);
export const listJournalEntriesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    status: journalEntryStatusSchema.optional(),
    entryType: journalEntryTypeSchema.optional(),
    source: z.string().max(40).optional().describe("MANUAL ya da modül adı"),
    sourceDocType: z.string().max(60).optional().describe("Kaynak belge türü (ör. EXPENSE, PURCHASE_INVOICE)"),
    sourceDocId: uuidSchema.optional().describe("Kaynak belgenin kimliği — belge detayındaki fiş bağlantısı"),
    dateFrom: localDateSchema.optional(),
    dateTo: localDateSchema.optional(),
    accountId: uuidSchema.optional(),
    search: z.string().trim().max(100).optional().describe("Açıklama ya da madde no"),
});
export const journalLineSourcesSchema = z.object({
    items: z.array(z.object({ sourceType: z.string(), sourceId: uuidSchema, amount: moneySchema })),
});
// ── Kayıt motoru ─────────────────────────────────────────────────────────────
export const postingLineSchema = z.object({
    key: postingKeyRefSchema,
    side: balanceSideSchema,
    amount: moneySchema.refine((value) => BigInt(value) > 0n, "Tutar sıfırdan büyük olmalıdır"),
    description: z.string().max(500).nullable().default(null),
    ...lineDimensionsShape,
    ...lineDocumentShape,
    /** Özet fişte satırı oluşturan kaynak işlemler (drill-down). */
    sources: z
        .array(z.object({
        sourceType: z.string().regex(/^[A-Z][A-Z0-9_]{1,59}$/),
        sourceId: uuidSchema,
        amount: moneySchema,
    }))
        .max(20_000)
        .default([]),
});
/** Diğer modüllerin `LedgerPostingService.post()` çağrısında verdiği istek. Kuyrukta da bu biçimde saklanır. */
export const postingRequestSchema = z.object({
    companyId: uuidSchema,
    postingDate: localDateSchema,
    documentDate: localDateSchema.nullable().default(null),
    entryType: journalEntryTypeSchema.default("MAHSUP"),
    description: z.string().min(1).max(500),
    source: z
        .string()
        .regex(/^[A-Z][A-Z0-9_]{1,39}$/)
        .describe("Modül adı: CASH, BANK, CHEQUE, INVOICE, FUEL, SHIFT…"),
    sourceDocType: z.string().regex(/^[A-Z][A-Z0-9_]{1,59}$/),
    sourceDocId: uuidSchema,
    sourcePurpose: z
        .string()
        .regex(/^[A-Z][A-Z0-9_]{1,39}$/)
        .default("MAIN")
        .describe("Aynı belgeden birden çok fiş doğuyorsa ayırt eder (ör. MAIN, COGS)"),
    isSummary: z.boolean().default(false),
    lines: z.array(postingLineSchema).min(2).max(2000),
});
export const accountMappingSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    postingKey: postingKeyNameSchema,
    keyParam: z.string().nullable(),
    stationId: uuidSchema.nullable(),
    stationName: z.string().nullable(),
    businessUnit: businessUnitTypeSchema.nullable(),
    accountId: uuidSchema,
    accountCode: z.string(),
    accountName: z.string(),
    note: z.string().nullable(),
    version: z.number().int(),
});
export const accountMappingListSchema = z.object({ items: z.array(accountMappingSchema) });
export const listAccountMappingsQuerySchema = companyQuery.extend({
    postingKey: postingKeyNameSchema.optional(),
});
export const createAccountMappingSchema = z.object({
    companyId: uuidSchema,
    postingKey: postingKeyNameSchema,
    keyParam: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9._-]{1,80}$/)
        .nullable()
        .default(null),
    stationId: uuidSchema.nullable().default(null),
    businessUnit: businessUnitTypeSchema.nullable().default(null),
    accountId: uuidSchema,
    note: z.string().trim().max(300).nullable().default(null),
});
export const updateAccountMappingSchema = z.object({
    accountId: uuidSchema.optional(),
    note: z.string().trim().max(300).nullable().optional(),
    version: z.number().int(),
});
export const postingKeyCatalogSchema = z.object({
    items: z.array(z.object({
        key: postingKeyNameSchema,
        label: z.string(),
        param: z.string().nullable(),
        module: z.string(),
    })),
});
export const POSTING_EXCEPTION_REASONS = [
    "MAPPING_MISSING",
    "ACCOUNT_NOT_POSTABLE",
    "DIMENSION_MISSING",
];
export const POSTING_EXCEPTION_STATUSES = ["PENDING", "RESOLVED", "DISMISSED"];
export const postingExceptionSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    source: z.string(),
    sourceDocType: z.string(),
    sourceDocId: uuidSchema,
    sourcePurpose: z.string(),
    postingDate: localDateSchema,
    description: z.string(),
    reason: z.enum(POSTING_EXCEPTION_REASONS),
    missingKeys: z.array(z.string()),
    detail: z.string().nullable(),
    totalAmount: moneySchema,
    status: z.enum(POSTING_EXCEPTION_STATUSES),
    attempts: z.number().int(),
    lastAttemptAt: timestampSchema,
    resolvedEntryId: uuidSchema.nullable(),
    createdAt: timestampSchema,
});
export const postingExceptionPageSchema = pageOf(postingExceptionSchema);
export const listPostingExceptionsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    status: z.enum(POSTING_EXCEPTION_STATUSES).default("PENDING"),
});
export const retryPostingExceptionsSchema = z.object({
    companyId: uuidSchema,
    ids: z.array(uuidSchema).max(500).optional().describe("Verilmezse bekleyenlerin tümü yeniden işlenir"),
});
export const retryPostingExceptionsResultSchema = z.object({
    attempted: z.number().int(),
    resolved: z.number().int(),
    stillPending: z.number().int(),
});
export const dismissPostingExceptionSchema = z.object({ reason: z.string().trim().min(5).max(500) });
// ── Açılış bakiyeleri ────────────────────────────────────────────────────────
export const OPENING_IMPORT_COLUMNS = [
    "Hesap Kodu",
    "Hesap Adı",
    "Borç",
    "Alacak",
    "İstasyon Kodu",
    "Açıklama",
];
export const openingImportRequestSchema = z.object({
    companyId: uuidSchema,
    fileId: uuidSchema.describe("`POST /files` ile yüklenmiş Excel/CSV dosyası"),
    postingDate: localDateSchema,
});
export const openingImportPreviewSchema = z.object({
    valid: z.boolean(),
    totalDebit: moneySchema,
    totalCredit: moneySchema,
    difference: moneySchema,
    rows: z.array(z.object({
        rowNo: z.number().int(),
        accountCode: z.string(),
        accountName: z.string().nullable(),
        debit: moneySchema,
        credit: moneySchema,
        stationCode: z.string().nullable(),
        errors: z.array(z.string()),
        notes: z
            .array(z.string())
            .describe("Hata olmayan yönlendirmeler (ör. cari ayrıntısı Cari modülünden girilir)"),
    })),
    errors: z.array(z.string()).describe("Dosya düzeyindeki hatalar (denge, mevcut açılış fişi…)"),
});
// ── Yıl sonu ─────────────────────────────────────────────────────────────────
export const YEAR_END_STEPS = ["COST_REFLECTION", "INCOME_CLOSING", "CLOSING_AND_OPENING"];
export const yearEndRequestSchema = z.object({ step: z.enum(YEAR_END_STEPS) });
export const yearEndResultSchema = z.object({
    step: z.enum(YEAR_END_STEPS),
    draftEntryIds: z.array(uuidSchema),
    notes: z.array(z.string()),
});
//# sourceMappingURL=accounting.js.map